import { formatUsdCost } from "./auth.js";
const AUTH_LABELS = {
    en: {
        username: "Username",
        password: "Password",
        inviteCode: "Invite code",
        login: "Login",
        createAccount: "Create Account",
        tabLogin: "Login",
        tabInvite: "Invite",
        tablistAria: "Authentication mode",
        whereItWent: "Where it went",
        categories: "categories",
        usageUnavailable: "Usage details unavailable.",
        usageThisPeriod: "Usage this period",
        usagePending: "Usage pending",
        remaining: "remaining",
        unlimited: "Unlimited",
        catImage: "Image generation",
        catChat: "Chat · Agent",
        catVoice: "Voice · TTS",
        catMusic: "Music",
        catVideo: "Video",
        catSfx: "Sound effects",
        catOther: "Other"
    },
    "zh-CN": {
        username: "用户名",
        password: "密码",
        inviteCode: "邀请码",
        login: "登录",
        createAccount: "注册账号",
        tabLogin: "登录",
        tabInvite: "邀请码注册",
        tablistAria: "登录方式",
        whereItWent: "用量去向",
        categories: "个类别",
        usageUnavailable: "暂无用量详情。",
        usageThisPeriod: "本期用量",
        usagePending: "用量统计中",
        remaining: "剩余",
        unlimited: "无限额",
        catImage: "图像生成",
        catChat: "对话 · 智能体",
        catVoice: "配音 · TTS",
        catMusic: "音乐",
        catVideo: "视频",
        catSfx: "音效",
        catOther: "其他"
    }
};
function authLabel(locale, key) {
    const dict = AUTH_LABELS[locale ?? "en"] ?? AUTH_LABELS.en;
    return dict[key];
}
export function escapeAuthHtml(value) {
    return value.replace(/[&<>"']/g, (char) => {
        switch (char) {
            case "&": return "&amp;";
            case "<": return "&lt;";
            case ">": return "&gt;";
            case "\"": return "&quot;";
            default: return "&#39;";
        }
    });
}
/* Inline SVG icons (~14px, stroke=currentColor) — mirrors the wl-card icon style. */
const SVG_OPEN = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">`;
const BREAKDOWN_ICONS = {
    // photo
    image: `${SVG_OPEN}<rect x="3" y="4.5" width="18" height="15" rx="2"/><circle cx="8.5" cy="9.5" r="1.6"/><path d="M4 17l4.5-4.5 4 4L16 12l4 4.5"/></svg>`,
    // chat bubble
    chat: `${SVG_OPEN}<path d="M5 4.5h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9l-4 3v-3a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2z"/></svg>`,
    // microphone
    voice: `${SVG_OPEN}<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M6 11a6 6 0 0 0 12 0M12 17v4M9 21h6"/></svg>`,
    // music note
    music: `${SVG_OPEN}<path d="M9 17V5l11-2v12"/><circle cx="6.5" cy="17" r="2.5"/><circle cx="17.5" cy="15" r="2.5"/></svg>`,
    // film
    video: `${SVG_OPEN}<rect x="3" y="4.5" width="18" height="15" rx="2"/><path d="M7.5 4.5v15M16.5 4.5v15M3 9.5h4.5M3 14.5h4.5M16.5 9.5H21M16.5 14.5H21"/></svg>`,
    // waveform — sound effects
    sfx: `${SVG_OPEN}<path d="M4 10v4M8 7v10M12 4v16M16 8v8M20 11v2"/></svg>`,
    // generic dot
    other: `${SVG_OPEN}<circle cx="12" cy="12" r="3.25"/></svg>`
};
// generic dot — fallback icon for unknown categories.
const BREAKDOWN_ICON_FALLBACK = BREAKDOWN_ICONS.other ?? "";
/** Map a spend-breakdown category key to its localized label key. */
const BREAKDOWN_LABEL_KEYS = {
    image: "catImage",
    chat: "catChat",
    voice: "catVoice",
    music: "catMusic",
    video: "catVideo",
    sfx: "catSfx",
    other: "catOther"
};
function buildBreakdownRows(breakdown, locale) {
    const total = Object.values(breakdown).reduce((sum, value) => sum + value, 0);
    return Object.entries(breakdown)
        .sort((a, b) => b[1] - a[1])
        .map(([key, amount]) => {
        const labelKey = Object.hasOwn(BREAKDOWN_LABEL_KEYS, key) ? BREAKDOWN_LABEL_KEYS[key] : undefined;
        const knownIcon = Object.hasOwn(BREAKDOWN_ICONS, key) ? BREAKDOWN_ICONS[key] : undefined;
        return {
            iconSvg: knownIcon ?? BREAKDOWN_ICON_FALLBACK,
            labelHtml: labelKey ? authLabel(locale, labelKey) : escapeAuthHtml(key),
            amount,
            percent: total > 0 ? Math.round((amount / total) * 100) : 0
        };
    });
}
function renderBreakdownRow(row) {
    return `
        <div class="wl-spend-row">
          <div class="wl-spend-head">
            <span class="wl-spend-ico" aria-hidden="true">${row.iconSvg}</span>
            <span class="wl-spend-label">${row.labelHtml}</span>
            <span class="wl-spend-amt">${formatUsdCost(row.amount)}</span>
            <span class="wl-spend-pct">${row.percent}%</span>
          </div>
          <div class="wl-spend-bar"><div class="wl-spend-bar__fill" style="width: ${row.percent}%"></div></div>
        </div>`;
}
function renderSpendBreakdown(breakdown, locale) {
    const rows = buildBreakdownRows(breakdown, locale);
    if (rows.length === 0) {
        return "";
    }
    return `
      <div class="wl-spend">
        <div class="wl-bill-row">
          <span class="wl-bill-cap">${authLabel(locale, "whereItWent")}</span>
          <span class="wl-bill-cap">${rows.length} ${authLabel(locale, "categories")}</span>
        </div>
        ${rows.map(renderBreakdownRow).join("")}
      </div>`;
}
export function renderAuthStatus(opts) {
    const okModifier = opts.badgeKind === "ok" ? " wl-status--ok" : "";
    const badgeClass = opts.badgeKind === "ok"
        ? "badge badge-success"
        : opts.badgeKind === "warn"
            ? "badge badge-warning"
            : "badge badge-neutral";
    return `
    <div class="wl-auth-status${okModifier}" role="status">
      <span class="wl-status__pulse" aria-hidden="true"></span>
      <span class="wl-auth-status__text">${escapeAuthHtml(opts.text)}</span>
      <span class="${badgeClass}">${escapeAuthHtml(opts.badgeLabel)}</span>
    </div>`;
}
export const AUTH_STYLES = String.raw `
    /* ===================================================================== */
    /* AUTH_STYLES — shared tokens, form scaffolding, buttons, badges, pulse, */
    /* auth chrome, banner, and usage panel. Used by the Welcome modal and    */
    /* the Account sidebar webview.                                            */
    /* ===================================================================== */
    :root {
      color-scheme: dark;

      /* — Field ramp (surfaces, darkest → lightest) — */
      --c-bg:            #0d1017;
      --c-surface:       #14181f;
      --c-surface-1:     #181d26;
      --c-surface-2:     #1d222d;
      --c-surface-3:     #232936;
      --c-surface-4:     #2a3140;
      --c-surface-inset: #0a0d13;

      /* — Hairlines — */
      --c-line:        rgba(206, 216, 238, 0.085);
      --c-line-strong: rgba(206, 216, 238, 0.16);

      /* — Text ramp — */
      --c-text:   #e6eaf2;
      --c-text-2: rgba(230, 234, 242, 0.72);
      --c-text-3: rgba(230, 234, 242, 0.55);
      --c-text-4: rgba(230, 234, 242, 0.34);

      /* — Ice-blue: THE interactive accent — */
      --c-accent:        #9fcaff;
      --c-accent-bright: #bcdcff;
      --c-accent-dim:    #6f9fd8;
      --c-accent-ink:    #06213d;
      --c-accent-wash:   rgba(159, 202, 255, 0.10);
      --c-accent-wash-2: rgba(159, 202, 255, 0.18);
      --c-accent-line:   rgba(159, 202, 255, 0.42);

      /* — Warm gold / cream: the single creative highlight — */
      --c-gold:        #ffdca7;
      --c-gold-bright: #ffe7c2;
      --c-gold-dim:    #c79e6a;
      --c-gold-ink:    #2a1c08;
      --c-gold-wash:   rgba(255, 220, 167, 0.12);

      /* — Status — */
      --c-success:      #9fd7ad;
      --c-warning:      #f1c27d;
      --c-danger:       #ffb4ab;
      --c-success-wash: rgba(159, 215, 173, 0.13);
      --c-danger-wash:  rgba(255, 180, 171, 0.13);

      /* — Atmosphere — */
      --atmos-shell:
        radial-gradient(1200px 680px at 18% -8%, rgba(159, 202, 255, 0.07), transparent 62%),
        radial-gradient(1000px 600px at 108% 6%, rgba(255, 220, 167, 0.045), transparent 60%),
        linear-gradient(180deg, #11151d 0%, var(--c-bg) 46%, #090c12 100%);
      --atmos-panel:
        linear-gradient(180deg, rgba(255, 255, 255, 0.022), rgba(255, 255, 255, 0) 120px),
        var(--c-surface);

      /* — Typography (system fallbacks; remote fonts blocked by CSP) — */
      --font-ui:      "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      --font-display: "Fraunces", "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, "Times New Roman", serif;

      --fz-micro: 10px; --fz-tiny: 11px; --fz-small: 12px; --fz-base: 13px;
      --fz-md: 15px; --fz-lg: 18px; --fz-xl: 22px;

      --lh-snug: 1.34; --lh-normal: 1.5; --lh-relaxed: 1.62;
      --ls-tight: -0.01em; --ls-caps: 0.12em;
      --fw-regular: 400; --fw-medium: 500; --fw-semibold: 600;

      /* — Spacing — */
      --sp-1: 4px; --sp-2: 8px; --sp-3: 12px; --sp-4: 16px;
      --sp-5: 20px; --sp-6: 24px; --sp-8: 32px; --sp-10: 40px;

      /* — Radii — */
      --r-xs: 3px; --r-sm: 5px; --r-md: 8px; --r-lg: 12px; --r-xl: 16px; --r-pill: 999px;

      /* — Elevation — */
      --shadow-1: 0 1px 2px rgba(0,0,0,0.30), 0 0 0 1px rgba(0,0,0,0.10);
      --shadow-2: 0 6px 18px -4px rgba(0,0,0,0.42), 0 2px 6px rgba(0,0,0,0.28);
      --shadow-3: 0 18px 44px -8px rgba(0,0,0,0.55), 0 6px 14px rgba(0,0,0,0.34);
      --shadow-pop: 0 24px 60px -12px rgba(0,0,0,0.62), 0 8px 20px rgba(0,0,0,0.4);
      --hl-top: inset 0 1px 0 rgba(255,255,255,0.05);
      --ring-focus: 0 0 0 2px var(--c-bg), 0 0 0 4px var(--c-accent);

      /* — Motion — */
      --ease-out:  cubic-bezier(0.22, 1, 0.36, 1);
      --ease-emph: cubic-bezier(0.2, 0.9, 0.1, 1);
      --dur-1: 90ms; --dur-2: 160ms; --dur-3: 260ms; --dur-4: 480ms;
    }

    *, *::before, *::after { box-sizing: border-box; }
    ::selection { background: var(--c-accent-wash-2); color: var(--c-text); }
    *:focus-visible { outline: none; box-shadow: var(--ring-focus); border-radius: var(--r-sm); }

    /* ---- Form scaffolding -------------------------------------------------- */
    .wl-form { display: grid; gap: var(--sp-4); }
    .wl-auth-form { margin-top: var(--sp-4); }
    .wl-field { display: grid; gap: var(--sp-1); }
    .wl-field__hint { font-size: var(--fz-micro); color: var(--c-text-3); margin-top: 2px; }
    .wl-label {
      display: block;
      font-size: var(--fz-tiny); font-weight: var(--fw-medium);
      color: var(--c-text-2); letter-spacing: var(--ls-tight);
    }
    .wl-input {
      display: block; width: 100%; min-height: 36px;
      padding: var(--sp-2) var(--sp-3);
      background: var(--c-surface-inset);
      border: 1px solid var(--c-line-strong);
      border-radius: var(--r-sm);
      color: var(--c-text);
      font-family: var(--font-ui);
      font-size: var(--fz-small);
      line-height: var(--lh-snug);
      transition: border-color var(--dur-2) var(--ease-out),
                  box-shadow var(--dur-2) var(--ease-out),
                  background-color var(--dur-2) var(--ease-out);
    }
    .wl-input::placeholder { color: var(--c-text-4); }
    .wl-input:hover { border-color: var(--c-accent-line); }
    .wl-input:focus {
      outline: none; border-color: var(--c-accent);
      background: var(--c-surface);
      box-shadow: 0 0 0 3px var(--c-accent-wash);
    }

    .wl-form__foot { display: flex; align-items: center; gap: var(--sp-3); margin-top: var(--sp-1); }
    .wl-form__foot .wl-btn { width: 100%; }
    .wl-form__foot--split .wl-btn { width: auto; flex: 1 1 0; }
    .wl-spring { flex: 1 1 auto; }

    /* ---- Buttons ----------------------------------------------------------- */
    .wl-btn {
      --btn-h: 30px;
      display: inline-flex; align-items: center; justify-content: center;
      gap: var(--sp-2);
      height: var(--btn-h);
      padding: 0 var(--sp-3);
      border: 1px solid var(--c-line-strong);
      border-radius: var(--r-sm);
      background: var(--c-surface-3);
      color: var(--c-text);
      font-family: var(--font-ui);
      font-size: var(--fz-tiny);
      font-weight: var(--fw-medium);
      letter-spacing: var(--ls-tight);
      line-height: 1;
      cursor: pointer; white-space: nowrap; user-select: none;
      transition: background-color var(--dur-1) var(--ease-out),
                  border-color var(--dur-1) var(--ease-out),
                  box-shadow var(--dur-1) var(--ease-out),
                  transform var(--dur-1) var(--ease-out),
                  filter var(--dur-1) var(--ease-out);
    }
    .wl-btn:hover { background: var(--c-surface-4); border-color: var(--c-line-strong); }
    .wl-btn:active { transform: translateY(0.5px); filter: brightness(0.96); }
    .wl-btn:focus-visible { box-shadow: var(--ring-focus); }
    .wl-btn--lg { --btn-h: 38px; font-size: var(--fz-small); padding: 0 var(--sp-5); }
    .wl-btn-primary {
      background: linear-gradient(180deg, var(--c-accent-bright), var(--c-accent));
      border-color: transparent; color: var(--c-accent-ink); font-weight: var(--fw-semibold);
      box-shadow: 0 1px 0 rgba(255, 255, 255, 0.18) inset, var(--shadow-1);
    }
    .wl-btn-primary:hover {
      background: linear-gradient(180deg, #cfe5ff, var(--c-accent-bright));
      box-shadow: 0 1px 0 rgba(255, 255, 255, 0.22) inset, var(--shadow-2), 0 0 22px -6px rgba(159, 202, 255, 0.6);
    }
    .wl-btn-primary:active { filter: brightness(0.97); }
    .wl-btn-gold {
      background: linear-gradient(180deg, var(--c-gold-bright), var(--c-gold));
      border-color: transparent; color: var(--c-gold-ink); font-weight: var(--fw-semibold);
      box-shadow: 0 1px 0 rgba(255, 255, 255, 0.25) inset, var(--shadow-1);
    }
    .wl-btn-gold:hover { box-shadow: 0 1px 0 rgba(255, 255, 255, 0.3) inset, var(--shadow-2), 0 0 22px -6px rgba(255, 220, 167, 0.6); }

    /* ---- Badges + pulse ---------------------------------------------------- */
    .badge {
      display: inline-flex; align-items: center; gap: var(--sp-1);
      height: 18px; padding: 0 var(--sp-2);
      border-radius: var(--r-xs);
      font-size: var(--fz-micro); font-weight: var(--fw-semibold);
      letter-spacing: 0.07em; text-transform: uppercase; line-height: 1; white-space: nowrap;
    }
    .badge-neutral { background: var(--c-surface-4); color: var(--c-text-2); }
    .badge-success { background: var(--c-success-wash); color: var(--c-success); }
    .badge-warning { background: rgba(241, 194, 125, 0.14); color: var(--c-warning); }
    .wl-status__pulse { position: relative; width: 8px; height: 8px; flex: none; }
    .wl-status__pulse::before {
      content: ""; position: absolute; inset: 0; border-radius: 50%;
      background: var(--c-warning); box-shadow: 0 0 8px -1px var(--c-warning);
    }
    .wl-status--ok .wl-status__pulse::before { background: var(--c-success); box-shadow: 0 0 8px -1px var(--c-success); }

    /* ---- Auth form chrome -------------------------------------------------- */
    .wl-auth-status {
      display: flex;
      align-items: center;
      gap: var(--sp-2);
      min-height: 34px;
      padding: 0 var(--sp-3);
      border: 1px solid var(--c-line);
      border-radius: var(--r-md);
      background: rgba(10, 13, 19, 0.74);
      color: var(--c-text-2);
      font-size: var(--fz-small);
    }
    .wl-auth-status__text { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .wl-auth-tabs {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 2px;
      margin-top: var(--sp-4);
      padding: 2px;
      border: 1px solid var(--c-line);
      border-radius: var(--r-md);
      background: rgba(10, 13, 19, 0.72);
    }
    .wl-auth-tab {
      height: 32px;
      border: 0;
      border-radius: calc(var(--r-md) - 3px);
      background: transparent;
      color: var(--c-text-3);
      font: inherit;
      font-size: var(--fz-small);
      font-weight: var(--fw-medium);
      cursor: pointer;
    }
    .wl-auth-tab:hover { color: var(--c-text); background: rgba(255, 255, 255, 0.035); }
    .wl-auth-tab.is-active {
      color: var(--c-text);
      background: var(--c-surface-3);
      box-shadow: 0 1px 0 rgba(255, 255, 255, 0.07) inset, var(--shadow-1);
    }
    .wl-auth-error {
      margin: var(--sp-4) 0 0;
      padding: var(--sp-2) var(--sp-3);
      border: 1px solid rgba(255, 112, 129, 0.24);
      border-radius: var(--r-md);
      background: rgba(255, 112, 129, 0.11);
      color: #ffb5bf;
      font-size: var(--fz-small);
      line-height: var(--lh-snug);
    }

    /* ---- Banner (Welcome top-of-page sign-in nudge) ---------------------- */
    .wl-banner {
      display: flex; align-items: center; gap: var(--sp-3);
      margin: 0 0 var(--sp-5);
      padding: var(--sp-2) var(--sp-3);
      border: 1px solid var(--c-accent-line);
      border-radius: var(--r-md);
      background: var(--c-accent-wash);
      color: var(--c-accent-bright);
      font-size: var(--fz-small);
    }
    .wl-banner__icon { display: inline-grid; place-items: center; color: var(--c-accent); }
    .wl-banner__text { flex: 1 1 auto; min-width: 0; color: var(--c-text-2); }
    .wl-banner__close {
      flex: none; width: 24px; height: 24px; display: grid; place-items: center;
      border: 0; border-radius: var(--r-sm); background: transparent;
      color: var(--c-text-3); font-size: 15px; line-height: 1; cursor: pointer;
    }
    .wl-banner__close:hover { background: var(--c-surface-3); color: var(--c-text); }
    .wl-btn--sm { --btn-h: 26px; font-size: var(--fz-tiny); padding: 0 var(--sp-3); }

    /* ---- Usage / billing panel (shared) ---------------------------------- */
    .wl-bill {
      border: 1px solid var(--c-line); border-radius: var(--r-md);
      background: var(--c-surface-1); padding: var(--sp-3) var(--sp-4);
      display: flex; flex-direction: column; gap: var(--sp-2);
    }
    .wl-bill-row { display: flex; align-items: baseline; justify-content: space-between; }
    .wl-bill-cap { font-size: var(--fz-micro); text-transform: uppercase; letter-spacing: var(--ls-caps); color: var(--c-text-3); }
    .wl-bill-per { font-size: var(--fz-tiny); color: var(--c-text-3); }
    .wl-bill-big { font-size: var(--fz-xl); font-weight: var(--fw-semibold); color: var(--c-text); letter-spacing: var(--ls-tight); }
    .wl-bill-big small { font-size: var(--fz-small); font-weight: var(--fw-regular); color: var(--c-text-3); }
    .wl-bill-track { height: 6px; border-radius: var(--r-pill); background: var(--c-surface-4); overflow: hidden; }
    .wl-bill-fill { height: 100%; background: var(--c-accent); border-radius: var(--r-pill); }
    .wl-bill-rem { font-size: var(--fz-tiny); color: var(--c-text-2); }
    .wl-bill-pending { font-size: var(--fz-small); color: var(--c-text-2); margin: 0; }
    .wl-bill-brk { display: flex; flex-direction: column; gap: var(--sp-1); border-top: 1px solid var(--c-line); padding-top: var(--sp-2); }
    .wl-bill-brk__row { display: flex; justify-content: space-between; font-size: var(--fz-tiny); color: var(--c-text-3); }
    .wl-bill-brk__row strong { color: var(--c-text-2); font-weight: var(--fw-medium); }
    .wl-bill-empty { font-size: var(--fz-small); color: var(--c-text-3); margin: 0; }

    /* ---- "Where it went" — spend-by-category breakdown -------------------- */
    .wl-spend { display: flex; flex-direction: column; gap: var(--sp-2); border-top: 1px solid var(--c-line); padding-top: var(--sp-3); }
    .wl-spend-row { display: flex; flex-direction: column; gap: var(--sp-1); }
    .wl-spend-head { display: flex; align-items: center; gap: var(--sp-2); }
    .wl-spend-ico {
      flex: none; width: 20px; height: 20px;
      display: grid; place-items: center;
      border-radius: var(--r-sm);
      background: var(--c-accent-wash);
      color: var(--c-accent);
    }
    .wl-spend-ico svg { display: block; }
    .wl-spend-label {
      flex: 1 1 auto; min-width: 0;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      font-size: var(--fz-small); color: var(--c-text-2);
    }
    .wl-spend-amt {
      flex: none; text-align: right;
      font-size: var(--fz-small); font-weight: var(--fw-medium); color: var(--c-text);
      font-variant-numeric: tabular-nums;
    }
    .wl-spend-pct {
      flex: none; width: 30px; text-align: right;
      font-size: var(--fz-tiny); color: var(--c-text-3);
      font-variant-numeric: tabular-nums;
    }
    .wl-spend-bar { height: 4px; border-radius: var(--r-pill); background: var(--c-surface-4); overflow: hidden; }
    .wl-spend-bar__fill { height: 100%; background: var(--c-accent); border-radius: var(--r-pill); }
`;
function renderLoginForm(locale) {
    return `
    <form class="wl-form wl-auth-form" id="login" novalidate>
      <div class="wl-field">
        <label class="wl-label" for="g-user">${authLabel(locale, "username")}</label>
        <input class="wl-input" id="g-user" name="username" autocomplete="username">
      </div>
      <div class="wl-field">
        <label class="wl-label" for="g-pass">${authLabel(locale, "password")}</label>
        <input class="wl-input" id="g-pass" name="password" type="password" autocomplete="current-password">
      </div>
      <div class="wl-form__foot">
        <button class="wl-btn wl-btn-primary wl-btn--lg" type="submit">${authLabel(locale, "login")}</button>
      </div>
    </form>`;
}
function renderInviteForm(locale) {
    return `
    <form class="wl-form wl-auth-form" id="register-invite" novalidate>
      <div class="wl-field">
        <label class="wl-label" for="r-user">${authLabel(locale, "username")}</label>
        <input class="wl-input" id="r-user" name="username" autocomplete="username">
      </div>
      <div class="wl-field">
        <label class="wl-label" for="r-pass">${authLabel(locale, "password")}</label>
        <input class="wl-input" id="r-pass" name="password" type="password" autocomplete="new-password">
      </div>
      <div class="wl-field">
        <label class="wl-label" for="r-invite">${authLabel(locale, "inviteCode")}</label>
        <input class="wl-input" id="r-invite" name="inviteCode" autocomplete="one-time-code">
      </div>
      <div class="wl-form__foot">
        <button class="wl-btn wl-btn-gold wl-btn--lg" type="submit">${authLabel(locale, "createAccount")}</button>
      </div>
    </form>`;
}
export function renderAuthForm(opts) {
    const isInvite = opts.mode === "invite";
    const { locale } = opts;
    return `
    <div class="wl-auth-tabs" role="tablist" aria-label="${escapeAuthHtml(authLabel(locale, "tablistAria"))}">
      <button class="wl-auth-tab${isInvite ? "" : " is-active"}" id="auth-tab-login" type="button" role="tab" aria-selected="${!isInvite}">${authLabel(locale, "tabLogin")}</button>
      <button class="wl-auth-tab${isInvite ? " is-active" : ""}" id="auth-tab-invite" type="button" role="tab" aria-selected="${isInvite}">${authLabel(locale, "tabInvite")}</button>
    </div>
    ${isInvite ? renderInviteForm(locale) : renderLoginForm(locale)}
    ${opts.error ? `<p class="wl-auth-error" role="alert">${escapeAuthHtml(opts.error)}</p>` : ""}`;
}
export function attachAuthFormHandlers(root, cb) {
    root.querySelector("#auth-tab-login")?.addEventListener("click", () => cb.onModeChange("login"));
    root.querySelector("#auth-tab-invite")?.addEventListener("click", () => cb.onModeChange("invite"));
    root.querySelector("#login")?.addEventListener("submit", (event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        cb.post({ type: `${cb.prefix}.login`, username: String(form.get("username") ?? ""), password: String(form.get("password") ?? "") });
    });
    root.querySelector("#register-invite")?.addEventListener("submit", (event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        cb.post({
            type: `${cb.prefix}.registerWithInvite`,
            username: String(form.get("username") ?? ""),
            password: String(form.get("password") ?? ""),
            inviteCode: String(form.get("inviteCode") ?? "")
        });
    });
}
export function renderUsagePanel(usage, locale) {
    if (!usage) {
        return `<p class="wl-bill-empty">${authLabel(locale, "usageUnavailable")}</p>`;
    }
    if (!("usedCostUsd" in usage)) {
        return `
      <div class="wl-bill">
        <div class="wl-bill-row"><span class="wl-bill-cap">${authLabel(locale, "usageThisPeriod")}</span><span class="wl-bill-per">${escapeAuthHtml(usage.period)}</span></div>
        <p class="wl-bill-pending">${authLabel(locale, "usagePending")}</p>
      </div>`;
    }
    const used = formatUsdCost(usage.usedCostUsd);
    const unlimited = usage.unlimited === true || usage.quotaCostUsd === null;
    const quota = unlimited ? authLabel(locale, "unlimited") : formatUsdCost(usage.quotaCostUsd);
    const pct = unlimited || !usage.quotaCostUsd
        ? 0
        : Math.min(100, Math.max(0, Math.round((usage.usedCostUsd / usage.quotaCostUsd) * 100)));
    const rawRemaining = typeof usage.remainingCostUsd === "number"
        ? usage.remainingCostUsd
        : unlimited ? undefined : usage.quotaCostUsd - usage.usedCostUsd;
    const remaining = rawRemaining === undefined ? undefined : Math.max(0, rawRemaining);
    const spendBreakdown = usage.breakdown ? renderSpendBreakdown(usage.breakdown, locale) : "";
    return `
    <div class="wl-bill">
      <div class="wl-bill-row"><span class="wl-bill-cap">${authLabel(locale, "usageThisPeriod")}</span><span class="wl-bill-per">${escapeAuthHtml(usage.period)}</span></div>
      <div class="wl-bill-big">${used} <small>/ ${quota}</small></div>
      <div class="wl-bill-track"><div class="wl-bill-fill" style="width: ${pct}%"></div></div>
      ${remaining !== undefined ? `<div class="wl-bill-rem">${formatUsdCost(remaining)} ${authLabel(locale, "remaining")}</div>` : ""}
      ${spendBreakdown}
    </div>`;
}
